import { FeedbackState } from '@ctrl-enter/types';
import { createLogger } from '@ctrl-enter/tools/utils';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

const logger = createLogger('web:store:feedback');

type FeedbackStoreState = FeedbackState & {
  // Actions
  incrementClickCount: () => void;
  resetClickCount: () => void;
  setLastShownVersion: (version: string) => void;
  setLastDismissedAt: (timestamp: string) => void;
  setDismissClickThreshold: (threshold: number) => void;
  setHasRatedCurrentVersion: (hasRated: boolean) => void;
  resetForNewVersion: (version: string) => void;
  clearAllData: () => void;
};

/**
 * Initial click threshold before showing popup
 */
const INITIAL_CLICK_THRESHOLD = 50;

/**
 * Additional clicks needed after "Remind me later"
 */
const REMIND_ME_LATER_THRESHOLD = 30;

/**
 * Feedback store
 * Tracks user clicks and feedback popup state
 * Shows popup after threshold clicks, once per app version
 */
export const useFeedbackStore = create<FeedbackStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        // Default state
        clickCount: 0,
        lastShownVersion: null,
        lastDismissedAt: null,
        dismissClickThreshold: INITIAL_CLICK_THRESHOLD,
        hasRatedCurrentVersion: false,

        // Increment click count
        incrementClickCount: () => {
          const current = get().clickCount;
          const newCount = current + 1;
          logger.info(`📊 [FEEDBACK-STORE] Click count: ${current} → ${newCount}`);
          set({ clickCount: newCount });
        },

        // Reset click count to 0
        resetClickCount: () => {
          logger.info('🔄 [FEEDBACK] Resetting click count');
          set({ clickCount: 0 });
        },

        // Set last shown version
        setLastShownVersion: version => {
          logger.info('📌 [FEEDBACK] Setting last shown version:', version);
          set({ lastShownVersion: version });
        },

        // Set last dismissed timestamp
        setLastDismissedAt: timestamp => {
          logger.info('⏰ [FEEDBACK] Setting last dismissed at:', timestamp);
          set({
            lastDismissedAt: timestamp,
            dismissClickThreshold: REMIND_ME_LATER_THRESHOLD,
          });
        },

        // Set dismiss click threshold
        setDismissClickThreshold: threshold => {
          logger.info('🎯 [FEEDBACK] Setting dismiss click threshold:', threshold);
          set({ dismissClickThreshold: threshold });
        },

        // Set whether user has rated current version
        setHasRatedCurrentVersion: hasRated => {
          logger.info('✅ [FEEDBACK] Setting has rated current version:', hasRated);
          set({ hasRatedCurrentVersion: hasRated });
        },

        // Reset state for new app version
        resetForNewVersion: version => {
          logger.info('🆕 [FEEDBACK] Resetting for new version:', version);
          set({
            clickCount: 0,
            lastShownVersion: null,
            lastDismissedAt: null,
            dismissClickThreshold: INITIAL_CLICK_THRESHOLD,
            hasRatedCurrentVersion: false,
          });
        },

        // Clear all data (on logout)
        clearAllData: () => {
          logger.info('🗑️ [FEEDBACK] Clearing all feedback data');
          set({
            clickCount: 0,
            lastShownVersion: null,
            lastDismissedAt: null,
            dismissClickThreshold: INITIAL_CLICK_THRESHOLD,
            hasRatedCurrentVersion: false,
          });
        },
      }),
      {
        name: 'ctrl-enter-feedback-store',
        version: 1,
      }
    ),
    { name: 'FeedbackStore' }
  )
);

// Atomic selectors
export const useClickCount = () => useFeedbackStore(state => state.clickCount);
export const useLastShownVersion = () => useFeedbackStore(state => state.lastShownVersion);
export const useLastDismissedAt = () => useFeedbackStore(state => state.lastDismissedAt);
export const useDismissClickThreshold = () =>
  useFeedbackStore(state => state.dismissClickThreshold);
export const useHasRatedCurrentVersion = () =>
  useFeedbackStore(state => state.hasRatedCurrentVersion);

// Action selectors
export const useIncrementClickCount = () => useFeedbackStore(state => state.incrementClickCount);
export const useResetClickCount = () => useFeedbackStore(state => state.resetClickCount);
export const useSetLastShownVersion = () => useFeedbackStore(state => state.setLastShownVersion);
export const useSetLastDismissedAt = () => useFeedbackStore(state => state.setLastDismissedAt);
export const useSetDismissClickThreshold = () =>
  useFeedbackStore(state => state.setDismissClickThreshold);
export const useSetHasRatedCurrentVersion = () =>
  useFeedbackStore(state => state.setHasRatedCurrentVersion);
export const useResetForNewVersion = () => useFeedbackStore(state => state.resetForNewVersion);
export const useClearFeedbackData = () => useFeedbackStore(state => state.clearAllData);
